import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and whether that element has come within `margin` of the
 * viewport. Stays true once reached. Always false during SSR and the first
 * client render, so markup matches on hydration.
 */
export function useNearViewport<T extends Element>(
    enabled = true,
    margin = '600px'
) {
    const ref = useRef<T>(null);
    const [isNear, setIsNear] = useState(false);

    useEffect(() => {
        if (!enabled || isNear) return;
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setIsNear(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setIsNear(true);
                    observer.disconnect();
                }
            },
            { rootMargin: `${margin} 0px` }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [enabled, isNear, margin]);

    return [ref, isNear] as const;
}
